/**
 * Declarative payload schemas for the campaign content-block admin — the DB-authoritative editing
 * counterpart to `resolveContent.ts`, which reassembles these same categories back into the runner's
 * shapes. This is the exact analog of `support/transformations/registry.ts` (declarative `ConfigField[]`
 * rendered by a generic `ConfigFieldForm`): here `CATEGORY_SCHEMAS` maps every content-block category the
 * Frontier runner READS to a friendly, per-field editor, so an admin never touches raw JSON.
 *
 * The field list for each category is derived from what `resolveContent.ts` actually reads back off the
 * block (payload keys) plus the block's own `label` (edited on the card, not here). Two categories carry
 * a question shape and delegate to the existing first-class question editors instead of scalar fields:
 * `scenario_question` (banded `ask`/`why`/`options` via `QuestionBlockEditor`) and the prose corpora
 * `recommended_question` / `deep_dive_question` (via `ProseQuestionEditor`).
 *
 * A category absent from `CATEGORY_SCHEMAS` (an ad-hoc/unknown category, or `ai_question_config` which
 * has its own dedicated editor) falls through to the graceful raw-JSON editor in `ContentBlocksEditor`.
 */

export type PayloadFieldType =
  | 'text'
  | 'textarea'
  | 'html'
  | 'number'
  | 'boolean'
  | 'select'
  | 'lines' // one value per line <-> string[]
  | 'chips' // multi-select <-> string[] (constrained to suggestions/refCategory unless `freeTags`)
  | 'capabilityRefs' // multi-select over the campaign's CampaignCapability keys <-> string[]
  | 'contentBlockRef' // single-select over one content-block category (see refCategory) <-> string
  | 'bandedOptions' // delegates to QuestionBlockEditor (ask/why/options)
  | 'proseQuestion'; // delegates to ProseQuestionEditor (question/why/category/when/pillar)

export interface PayloadFieldOption {
  value: string;
  label: string;
}

export interface PayloadFieldDef {
  /** Payload key this field reads/writes. Ignored for the delegated `bandedOptions`/`proseQuestion`. */
  key: string;
  label: string;
  type: PayloadFieldType;
  helperText?: string;
  placeholder?: string;
  /** For `select` (and the fallback option set of a `contentBlockRef` when no blocks are authored). */
  options?: PayloadFieldOption[];
  /** Suggested values for `chips`. */
  suggestions?: string[];
  /** For `chips`: allow free-typed tags in addition to the suggestions/refCategory options. */
  freeTags?: boolean;
  /**
   * For `contentBlockRef` and `chips`: the content-block category whose blocks are the option set,
   * so the picker is DB-driven (the actually-authored blocks) rather than a hard-coded list.
   */
  refCategory?: string;
  /** For `contentBlockRef`/`chips` refCategory: which payload key of a referenced block holds the
   *  stored value (defaults to the block's own `key`). */
  refValueKey?: string;
  /** For `contentBlockRef`/`chips` refCategory: which payload key labels a referenced block in the
   *  dropdown (defaults to the block's `label`). */
  refLabelKey?: string;
  /** For a `text` field that is a stable id others key on (e.g. `gap_row.slug`): flag an empty value
   *  in the editor, since an empty id silently breaks the link the field is supposed to provide. */
  warnIfEmpty?: boolean;
}

export interface CategorySchema {
  /** Human label for the category (shown as the editor heading). */
  title: string;
  /** One line describing what blocks in this category are. */
  description?: string;
  fields: PayloadFieldDef[];
}

/** Deep-dive answer bands a scenario question is scored against (matches migration 0137 `_band_options`). */
const BAND_OPTIONS: PayloadFieldOption[] = [
  { value: 'gap', label: 'gap' },
  { value: 'partial', label: 'partial' },
  { value: 'covered', label: 'covered' },
];

/** Gap-row starting status the runner recognizes (`GapRowDef['defaultStatus']`). */
const GAP_STATUS_OPTIONS: PayloadFieldOption[] = [
  { value: 'notstarted', label: 'Not started' },
  { value: 'progress', label: 'In progress' },
  { value: 'done', label: 'Done' },
];

/** The deep-dive phases (`DeepDivePhase`) a closing item is grouped under. */
const CLOSING_PHASE_OPTIONS: PayloadFieldOption[] = [
  { value: 'assess', label: 'Assess' },
  { value: 'act', label: 'Act' },
  { value: 'optimize', label: 'Optimize' },
];

/** The stakeholder ids a deep-dive question's `who` can point at (matches `deep_dive_stakeholder` keys). */
const STAKEHOLDER_IDS = ['infra', 'risk', 'netarch', 'lob', 'proc'];

/**
 * The Cisco IQ scenario states a question can be gated to (`IqScenario` in the runner's
 * `config/content.ts`; the runner compares `payload.when`/`payload.iq` against exactly these). Used as
 * the fallback option set for the `iq_scenario`-backed `contentBlockRef` fields when a campaign hasn't
 * authored its own `iq_scenario` blocks. Keep in sync with `IqScenario`.
 */
const IQ_SCENARIO_OPTIONS: PayloadFieldOption[] = [
  { value: 'active', label: 'Active (IQ connected)' },
  { value: 'enabled-unused', label: 'Enabled but unused' },
  { value: 'not-enabled', label: 'Not enabled' },
];

/** The deep-dive stakeholder priority tiers (display-weighted; a small fixed set). */
const STAKEHOLDER_TIER_OPTIONS: PayloadFieldOption[] = [
  { value: 'Highly recommended', label: 'Highly recommended' },
  { value: 'Recommended', label: 'Recommended' },
  { value: 'Optional', label: 'Optional' },
];

/** The phase a sequencing tip is grouped under — the deep-dive phases plus `any` (shown always). */
const SEQUENCING_PHASE_OPTIONS: PayloadFieldOption[] = [
  { value: 'any', label: 'Any' },
  ...CLOSING_PHASE_OPTIONS,
];

export const CATEGORY_SCHEMAS: Record<string, CategorySchema> = {
  // --- content.ts corpora -------------------------------------------------
  connect_tool: {
    title: 'Connect tool card',
    description: 'A "Connect your tools" card in the Profiling step.',
    fields: [
      { key: 'name', label: 'Tool name', type: 'text' },
      { key: 'url', label: 'URL', type: 'text' },
      { key: 'linkNote', label: 'Link note (optional)', type: 'text', helperText: 'e.g. "VPN required"' },
      { key: 'howTo', label: 'How-to steps (optional, one per line)', type: 'lines' },
      { key: 'howToTitle', label: 'How-to heading (optional)', type: 'text', helperText: 'Defaults to "How to check" — e.g. "How to get the file" for a download procedure' },
      { key: 'confirmIdleLabel', label: 'Confirm idle label', type: 'text', helperText: 'e.g. "Customer is IQ enabled"' },
      { key: 'confirmDoneLabel', label: 'Confirm done label', type: 'text', helperText: 'e.g. "✓ IQ enabled"' },
      { key: 'confirmOnLabel', label: 'Confirm on label', type: 'text', helperText: 'e.g. "IQ enabled"' },
      { key: 'confirmOffLabel', label: 'Confirm off label', type: 'text', helperText: 'e.g. "Not confirmed"' },
      { key: 'denyIdleLabel', label: 'Deny idle label (optional)', type: 'text', helperText: 'e.g. "Customer is not IQ enabled"' },
      { key: 'denyDoneLabel', label: 'Deny done label (optional)', type: 'text', helperText: 'e.g. "✓ Not IQ enabled"' },
      { key: 'denyOnLabel', label: 'Deny on label (optional)', type: 'text', helperText: 'e.g. "Not IQ enabled"' },
    ],
  },
  gap_row: {
    title: 'Gap row',
    description: 'A row in the current-state gap table (the live install-base figure is code-driven).',
    fields: [
      { key: 'slug', label: 'Slug', type: 'text', helperText: 'Stable id; also keys the live figure', warnIfEmpty: true },
      { key: 'category', label: 'Category', type: 'text' },
      { key: 'currentState', label: 'Current state', type: 'textarea' },
      { key: 'gap', label: 'Gap', type: 'textarea' },
      { key: 'action', label: 'Action', type: 'textarea' },
      { key: 'defaultStatus', label: 'Default status', type: 'select', options: GAP_STATUS_OPTIONS },
    ],
  },
  agenda: {
    title: 'Agenda item',
    fields: [
      { key: 'title', label: 'Title', type: 'text' },
      { key: 'duration', label: 'Duration', type: 'text', helperText: 'e.g. "20 min" — the number is summed for the total' },
      { key: 'desc', label: 'Description', type: 'textarea' },
    ],
  },
  next_step: {
    title: 'Next step',
    fields: [{ key: 'text', label: 'Text', type: 'textarea' }],
  },
  nudge: {
    title: 'Workshop Guide nudge',
    description:
      "A short line the Workshop Guide surfaces at a moment in the run. The block's KEY names the slot: " +
      'the singletons "welcome", "liveexit", "idle"; the per-step families "intro:<stepKind>", ' +
      '"videodone:<stepKind>", "live:<stepKind>"; and the per-phase "phasedone:<phase>". A slot with no ' +
      'block simply fires no nudge.',
    fields: [{ key: 'text', label: 'Nudge text', type: 'textarea', warnIfEmpty: true }],
  },
  faq: {
    title: 'FAQ',
    description:
      'One question/answer pair for the optional FAQ section. Pairs with the same Group are shown together under that heading. Add the "faq" primitive to a step\'s layout to render these.',
    fields: [
      { key: 'group', label: 'Group (optional)', type: 'text', helperText: 'Heading to bucket related Q/A under, e.g. "Cisco Ready"' },
      { key: 'q', label: 'Question', type: 'textarea', warnIfEmpty: true },
      { key: 'a', label: 'Answer', type: 'textarea', warnIfEmpty: true },
    ],
  },
  discovery_question: {
    title: 'Discovery question',
    fields: [{ key: 'text', label: 'Question', type: 'textarea' }],
  },
  prereq_question: {
    title: 'Prerequisite question',
    fields: [{ key: 'text', label: 'Question', type: 'textarea' }],
  },
  stakeholder_priority: {
    title: 'Stakeholder priority labels',
    description: 'The display label shown for each stakeholder priority tier.',
    fields: [
      { key: 'must', label: 'Must-have label', type: 'text' },
      { key: 'should', label: 'Should-have label', type: 'text' },
      { key: 'nice', label: 'Nice-to-have label', type: 'text' },
    ],
  },
  iq_scenario: {
    title: 'Cisco IQ scenario',
    fields: [
      { key: 'id', label: 'Id', type: 'text', helperText: 'Stable id the runner keys on' },
      { key: 'label', label: 'Label', type: 'text' },
      { key: 'detail', label: 'Detail', type: 'textarea' },
      { key: 'prep', label: 'Prep guidance', type: 'textarea' },
      { key: 'question', label: 'Question text (optional)', type: 'textarea', helperText: 'The IQ question shown in Propose when this scenario is possible' },
      { key: 'why', label: 'Why text (optional)', type: 'textarea', helperText: 'The rationale shown below the IQ question' },
    ],
  },
  recommended_question: {
    title: 'Recommended (Propose) question',
    description: 'A prose proposal-call question, optionally gated to a Cisco IQ scenario.',
    fields: [{ key: '', label: '', type: 'proseQuestion' }],
  },
  deep_dive_question: {
    title: 'Deep-dive question',
    description: 'A prose deep-dive question grouped under a pillar.',
    fields: [{ key: '', label: '', type: 'proseQuestion' }],
  },
  question_bank: {
    title: 'Question-bank group',
    description: 'A labelled group of workshop questions (one question per line).',
    fields: [
      { key: 'label', label: 'Group label', type: 'text' },
      {
        key: 'iq',
        label: 'Cisco IQ gate (optional)',
        type: 'contentBlockRef',
        refCategory: 'iq_scenario',
        refValueKey: 'id',
        options: IQ_SCENARIO_OPTIONS,
        helperText: 'Leave blank to always show; otherwise the Cisco IQ scenario that gates this group',
      },
      { key: 'questions', label: 'Questions (one per line)', type: 'lines' },
    ],
  },

  // --- deep dive ----------------------------------------------------------
  deep_dive_stakeholder: {
    title: 'Deep-dive stakeholder',
    fields: [
      { key: 'id', label: 'Id', type: 'text', helperText: 'Stable id referenced by a question’s "who"' },
      { key: 'label', label: 'Label', type: 'text' },
      { key: 'tier', label: 'Priority tier', type: 'select', options: STAKEHOLDER_TIER_OPTIONS },
    ],
  },
  scenario_section: {
    title: 'Deep-dive section',
    description: 'A section of deep-dive questions. Its title is the block Label above.',
    fields: [
      { key: 'code', label: 'Code', type: 'text', helperText: 'e.g. S1' },
      { key: 'group', label: 'Group heading', type: 'text' },
      { key: 'short', label: 'Short label', type: 'text' },
      { key: 'why', label: 'Why it matters', type: 'html', helperText: 'HTML allowed (e.g. <b>…</b>)' },
    ],
  },
  scenario_question: {
    title: 'Deep-dive question',
    description: 'A banded question. Its parent section is set by the Parent above.',
    fields: [
      {
        key: 'who',
        label: 'Who to ask',
        type: 'chips',
        helperText: 'The deep-dive stakeholders this question is aimed at',
        refCategory: 'deep_dive_stakeholder',
        refValueKey: 'id',
        suggestions: STAKEHOLDER_IDS,
      },
      { key: '', label: '', type: 'bandedOptions' },
    ],
  },
  scenario: {
    title: 'Deep-dive scenario',
    description: 'A scenario. Its title is the block Label above; phases are child scenario_phase blocks.',
    fields: [
      { key: 'n', label: 'Number', type: 'text', helperText: 'e.g. 01' },
      { key: 'sub', label: 'Subtitle', type: 'textarea' },
      { key: 'setup', label: 'Setup', type: 'textarea' },
    ],
  },
  scenario_phase: {
    title: 'Scenario phase',
    description: 'A phase within a scenario. Its label is the block Label; parent is the scenario above.',
    fields: [
      { key: 'n', label: 'Number', type: 'text', helperText: 'e.g. 01' },
      { key: 'lead', label: 'Lead-in', type: 'textarea' },
      { key: 'sections', label: 'Section ids (one per line)', type: 'lines', helperText: 'scenario_section keys included in this phase' },
    ],
  },
  sequencing: {
    title: 'Sequencing note',
    description: 'A sequencing tip (the "fallback" block only uses Text).',
    fields: [
      { key: 'phase', label: 'Phase', type: 'select', options: SEQUENCING_PHASE_OPTIONS },
      { key: 'plays', label: 'Plays', type: 'capabilityRefs', helperText: 'Capabilities this tip sequences' },
      { key: 'text', label: 'Text', type: 'html', helperText: 'HTML allowed' },
    ],
  },
  closing: {
    title: 'Closing item',
    fields: [
      { key: 'phase', label: 'Phase', type: 'select', options: CLOSING_PHASE_OPTIONS },
      { key: 'title', label: 'Title', type: 'text' },
      { key: 'body', label: 'Body', type: 'html', helperText: 'HTML allowed' },
    ],
  },
  pushback: {
    title: 'Pushback / objection',
    fields: [
      { key: 'objection', label: 'Objection', type: 'textarea' },
      { key: 'response', label: 'Response', type: 'html', helperText: 'HTML allowed' },
      { key: 'why', label: 'Why this response', type: 'text' },
    ],
  },
};

/** Whether a category has a friendly schema (vs. falling through to the raw-JSON editor). */
export const hasCategorySchema = (category: string): boolean => category in CATEGORY_SCHEMAS;
